import { useEffect } from 'react'
import { path, values } from 'ramda'

import { CardBrand, Method } from '@/types'
import { RootState, useDispatch, useSelector } from '@/lib/redux'

export type PaymentMethod = Omit<Partial<Method>, 'brand'> & {
  card_last4?: string
  brand?: CardBrand
}

const isType = (type?: string): type is CardBrand =>
  typeof type === 'string' &&
  ['visa', 'mastercard', 'american-express', 'unionpay', 'unknown'].indexOf(
    type
  ) >= 0

const formatType = (type?: string): CardBrand => {
  if (type === 'amex') return 'american-express'
  if (!isType(type)) return 'unknown'
  return type
}

const getLength = (brand?: string) => {
  if (brand === 'american-express') return 15
  if (brand === 'mastercard') return 16
  // Visa can be 13 or 16, but is 16 is more common
  if (brand === 'visa') return 16
  // UnionPay can be 16-19, but is 16 is more common
  if (brand === 'unionpay') return 16
  return 16
}

const getGaps = (length: number) => {
  if (length === 16) return [4, 4, 4, 4]
  if (length === 15) return [4, 6, 5]
  return [4, 4, 4, length - 12]
}

const guessLength = (last4?: string, brand?: string): string => {
  if (!last4) return ''
  const length = getLength(brand)
  const gaps = getGaps(length)
  const placeholder = '*'.repeat(length - 4) + last4
  let value = ''
  let count = 0
  let gapIndex = 0
  for (let i = 0; i < placeholder.length; i++) {
    value = value + placeholder[i]
    count++
    if (gaps[gapIndex] && count >= gaps[gapIndex]) {
      gapIndex++
      count = 0
      value = value + ' '
    }
  }
  return value
}

const select = (data: Method[]): PaymentMethod => {
  if (!data) return {}
  const card = data.find(method => method.is_default)
  if (!card) return {}
  const { card_number, brand, ...rest } = card
  const cardBrand = formatType(brand)
  const final: PaymentMethod = {
    card_number: guessLength(card_number, cardBrand),
    card_last4: card_number,
    brand: cardBrand,
    ...rest
  }
  return final
}

const selector = (state: RootState): PaymentMethod | undefined => {
  const methods = path<Record<string, Method>>(['plans', 'payment_methods'])(
    state
  )
  if (!methods) return
  return select(values(methods))
}

type PaymentProps = {
  loading: boolean
  data: PaymentMethod
}

const usePaymentMethods = (): PaymentProps => {
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch.plans.getPaymentMethods()
  }, [dispatch.plans])
  const data = useSelector<PaymentMethod | undefined>(selector)
  return { data: data || {}, loading: !data }
}

export default usePaymentMethods
